/**
 * ShopStore - MobX store for shop management.
 *
 * Handles buying, selling, and buyback of items.
 */

import {
  type ShopDefinition,
  type BuybackItem,
  type ItemDefinition,
  type ShopInventoryItem,
  TRANSACTION_CONFIRM_THRESHOLD,
} from '@idle-depths/shared';
import { makeAutoObservable, runInAction } from 'mobx';

import type { RootStore } from './RootStore';

/**
 * ShopStore manages shop interactions.
 */
export class ShopStore {
  /** Current shop (null if none open) */
  currentShop: ShopDefinition | null = null;

  /** Alias for currentShop */
  get activeShop(): ShopDefinition | null {
    return this.currentShop;
  }

  /** Alias for buybackItem */
  get lastSoldItem(): BuybackItem | null {
    return this.buybackItem;
  }

  /** Stock state (runtime tracking) */
  stockState: Map<string, number> = new Map();

  /** Last item sold (for buyback) */
  buybackItem: BuybackItem | null = null;

  /** Whether loading shop data */
  isLoading = false;

  /** Last error */
  error: string | null = null;

  /** Reference to root store */
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      rootStore: false,
    } as Record<string, false>);
  }

  // ===========================================================================
  // Computed Properties
  // ===========================================================================

  /**
   * Whether a shop is open.
   */
  get isOpen(): boolean {
    return this.currentShop !== null;
  }

  /**
   * Get shop inventory with current stock.
   */
  get shopInventory(): ShopInventoryItem[] {
    if (!this.currentShop) {
      return [];
    }

    return this.currentShop.inventory.map((item) => ({
      ...item,
      currentStock: this.stockState.get(item.itemId) ?? item.stockLimit ?? Infinity,
    }));
  }

  /**
   * Alias for shopInventory.
   */
  get shopItems(): ShopInventoryItem[] {
    return this.shopInventory;
  }

  // ===========================================================================
  // Shop Management
  // ===========================================================================

  /**
   * Initialize shop for a specific shop ID.
   *
   * @param shopId - Shop ID to open
   * @returns True if successful
   */
  initialize(shopId: string): boolean {
    // Find shop by ID
    const shops = this.rootStore.gameStore.content.getAll<ShopDefinition>('shops');
    const shop = shops.find((s) => s.id === shopId);

    if (!shop) {
      this.error = 'Shop not found';
      return false;
    }

    this.currentShop = shop;
    this.error = null;

    // Initialize stock state
    this.stockState.clear();
    for (const item of shop.inventory) {
      if (item.stockLimit !== null) {
        this.stockState.set(item.itemId, item.stockLimit);
      }
    }

    return true;
  }

  /**
   * Open shop for current location.
   */
  openShop(): boolean {
    const locationId = this.rootStore.characterStore.activeCharacter?.currentLocationId;
    if (!locationId) {
      this.error = 'No active character';
      return false;
    }

    // Find shop for this location
    const shops = this.rootStore.gameStore.content.getAll<ShopDefinition>('shops');
    const shop = shops.find((s) => s.locationId === locationId);

    if (!shop) {
      this.error = 'No shop available in this location';
      this.rootStore.notificationStore.error('Shop', 'No shop available in this location');
      return false;
    }

    runInAction(() => {
      this.currentShop = shop;
      this.error = null;

      // Initialize stock state
      this.stockState.clear();
      for (const item of shop.inventory) {
        if (item.stockLimit !== null) {
          this.stockState.set(item.itemId, item.stockLimit);
        }
      }
    });

    return true;
  }

  /**
   * Close the current shop.
   */
  closeShop(): void {
    this.currentShop = null;
    this.error = null;
    // Don't clear buyback - it persists until character switch
  }

  // ===========================================================================
  // Stock Management
  // ===========================================================================

  /**
   * Check if item is in stock.
   */
  isInStock(itemId: string): boolean {
    if (!this.currentShop) {
      return false;
    }

    const shopItem = this.currentShop.inventory.find((i) => i.itemId === itemId);
    if (!shopItem) {
      return false;
    }

    if (shopItem.stockLimit === null) {
      return true; // Infinite stock
    }

    const currentStock = this.stockState.get(itemId) ?? 0;
    return currentStock > 0;
  }

  /**
   * Get current stock of an item.
   */
  getStock(itemId: string): number {
    if (!this.currentShop) {
      return 0;
    }

    const shopItem = this.currentShop.inventory.find((i) => i.itemId === itemId);
    if (!shopItem) {
      return 0;
    }

    if (shopItem.stockLimit === null) {
      return Infinity;
    }

    return this.stockState.get(itemId) ?? 0;
  }

  // ===========================================================================
  // Buying
  // ===========================================================================

  /**
   * Get buy price for an item.
   */
  getBuyPrice(itemId: string): number {
    if (!this.currentShop) {
      return 0;
    }

    const shopItem = this.currentShop.inventory.find((i) => i.itemId === itemId);
    const itemDef = this.rootStore.gameStore.content.get<ItemDefinition>('items', itemId);

    if (!itemDef) {
      return 0;
    }

    return shopItem?.priceOverride ?? itemDef.value;
  }

  /**
   * Buy item from shop.
   *
   * @param itemId - Item to buy
   * @param quantity - Quantity to buy
   * @returns True if successful
   */
  buyItem(itemId: string, quantity: number): boolean {
    if (!this.currentShop || quantity <= 0) {
      return false;
    }

    const shopItem = this.currentShop.inventory.find((i) => i.itemId === itemId);
    if (!shopItem) {
      this.error = 'Item not found in shop';
      return false;
    }

    // Check stock
    if (shopItem.stockLimit !== null) {
      const available = this.stockState.get(itemId) ?? 0;
      if (available < quantity) {
        this.error = `Only ${String(available)} in stock`;
        this.rootStore.notificationStore.error('Insufficient Stock', this.error);
        return false;
      }
    }

    const itemDef = this.rootStore.gameStore.content.get<ItemDefinition>('items', itemId);
    if (!itemDef) {
      this.error = 'Item definition not found';
      return false;
    }

    const pricePerItem = shopItem.priceOverride ?? itemDef.value;
    const totalCost = pricePerItem * quantity;

    // Check funds
    if (!this.rootStore.characterStore.spendCurrency(totalCost)) {
      this.error = 'Insufficient funds';
      this.rootStore.notificationStore.error(
        'Insufficient Funds',
        `You need ${String(totalCost)} gold`
      );
      return false;
    }

    // Reduce stock
    if (shopItem.stockLimit !== null) {
      const current = this.stockState.get(itemId) ?? 0;
      this.stockState.set(itemId, current - quantity);
    }

    // Add to bank
    this.rootStore.bankStore.addItem(itemId, quantity);

    // Notify
    this.rootStore.notificationStore.success(
      'Purchase Complete',
      `Bought ${String(quantity)}x ${itemDef.name} for ${String(totalCost)}g`
    );

    this.error = null;
    return true;
  }

  /**
   * Check if purchase needs confirmation (expensive).
   */
  needsConfirmation(totalPrice: number): boolean {
    return totalPrice > TRANSACTION_CONFIRM_THRESHOLD;
  }

  // ===========================================================================
  // Selling
  // ===========================================================================

  /**
   * Get sell price for an item.
   */
  getSellPrice(itemId: string): number {
    if (!this.currentShop) {
      return 0;
    }

    const itemDef = this.rootStore.gameStore.content.get<ItemDefinition>('items', itemId);
    if (!itemDef) {
      return 0;
    }

    return Math.floor(itemDef.value * this.currentShop.sellPriceMultiplier);
  }

  /**
   * Sell item to shop.
   *
   * @param itemId - Item to sell
   * @param quantity - Quantity to sell
   * @returns True if successful
   */
  sellItem(itemId: string, quantity: number): boolean {
    if (!this.currentShop || quantity <= 0) {
      return false;
    }

    // Check if item is in bank
    if (!this.rootStore.bankStore.hasItem(itemId, quantity)) {
      this.error = 'Item not in bank';
      return false;
    }

    const itemDef = this.rootStore.gameStore.content.get<ItemDefinition>('items', itemId);
    if (!itemDef) {
      this.error = 'Item definition not found';
      return false;
    }

    // Check if tradeable
    if (!itemDef.tradeable) {
      this.error = 'Cannot sell this item';
      this.rootStore.notificationStore.error('Cannot Sell', 'This item cannot be sold');
      return false;
    }

    const sellPrice = Math.floor(itemDef.value * this.currentShop.sellPriceMultiplier);
    const totalEarned = sellPrice * quantity;

    // Remove from bank
    if (!this.rootStore.bankStore.removeItem(itemId, quantity)) {
      this.error = 'Failed to remove item from bank';
      return false;
    }

    // Add currency
    this.rootStore.characterStore.addCurrency(totalEarned);

    // Set buyback
    if (this.currentShop.buybackEnabled) {
      this.buybackItem = {
        itemId,
        quantity,
        soldPrice: sellPrice,
        soldAt: Date.now(),
      };
    }

    // Notify
    this.rootStore.notificationStore.success(
      'Sale Complete',
      `Sold ${String(quantity)}x ${itemDef.name} for ${String(totalEarned)}g`
    );

    this.error = null;
    return true;
  }

  // ===========================================================================
  // Buyback
  // ===========================================================================

  /**
   * Buyback the last sold item.
   *
   * @returns True if successful
   */
  buyback(): boolean {
    if (!this.buybackItem) {
      this.error = 'No item to buyback';
      return false;
    }

    const totalCost = this.buybackItem.soldPrice * this.buybackItem.quantity;

    // Check funds
    if (!this.rootStore.characterStore.spendCurrency(totalCost)) {
      this.error = 'Insufficient funds';
      this.rootStore.notificationStore.error(
        'Insufficient Funds',
        `Need ${String(totalCost)}g for buyback`
      );
      return false;
    }

    // Add to bank
    this.rootStore.bankStore.addItem(this.buybackItem.itemId, this.buybackItem.quantity);

    // Notify
    const itemDef = this.rootStore.gameStore.content.get<ItemDefinition>(
      'items',
      this.buybackItem.itemId
    );
    this.rootStore.notificationStore.success(
      'Buyback Complete',
      `Bought back ${String(this.buybackItem.quantity)}x ${itemDef?.name ?? 'item'}`
    );

    // Clear buyback
    this.buybackItem = null;
    this.error = null;
    return true;
  }

  /**
   * Alias for buyback().
   */
  buybackLastSoldItem(): boolean {
    return this.buyback();
  }

  /**
   * Clear buyback without buying back.
   */
  clearBuyback(): void {
    this.buybackItem = null;
  }

  // ===========================================================================
  // Reset
  // ===========================================================================

  /**
   * Reset store to initial state.
   */
  reset(): void {
    this.currentShop = null;
    this.stockState.clear();
    this.buybackItem = null;
    this.isLoading = false;
    this.error = null;
  }
}
